import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { canShowPlans, isNativeApp } from "@/lib/platform";
import { openPaywall } from "@/lib/iap";

const DISMISS_KEY = "bm_trial_ending_dismissed";

function daysLeft(iso) {
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

/**
 * TrialEndingBanner — conversion nudge for the last 2 days of the 7-day
 * signup Pro trial. One-tap annual upgrade (straight to Stripe checkout on
 * web, RevenueCat paywall on native). Dismissible per session.
 */
export default function TrialEndingBanner() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [busy, setBusy] = useState(false);
    const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === "1");

    // Only for free-status users still inside a Pro window that lapses within 2 days
    if (dismissed || !canShowPlans() || !user?.pro_until) return null;
    if (user.subscription_status !== "free" || user.plan_tier !== "pro") return null;
    const days = daysLeft(user.pro_until);
    if (days > 2 || days <= 0) return null;

    const upgradeAnnual = async () => {
        if (isNativeApp()) {
            openPaywall();
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/billing/checkout", {
                plan: "annual",
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch {
            setBusy(false);
            nav("/pricing");
        }
    };

    const dismiss = () => {
        sessionStorage.setItem(DISMISS_KEY, "1");
        setDismissed(true);
    };

    return (
        <div
            data-testid="trial-ending-banner"
            role="alert"
            style={{
                background: "linear-gradient(90deg, rgba(212, 181, 114, 0.16), rgba(212, 181, 114, 0.06))",
                borderBottom: "1px solid var(--bm-gold-dim)",
                padding: "11px 44px 11px 24px",
                display: "flex",
                gap: 14,
                alignItems: "center",
                justifyContent: "center",
                flexWrap: "wrap",
                position: "relative",
            }}
        >
            <Sparkles size={16} color="var(--bm-gold)" />
            <div style={{ color: "var(--bm-text)", fontSize: 14, fontWeight: 500 }}>
                Your <strong>Pro trial ends {days === 1 ? "tomorrow" : `in ${days} days`}</strong> — keep unlimited
                vessels, AI scanning &amp; charts for less than a shackle a month.
            </div>
            <button
                data-testid="trial-ending-upgrade-btn"
                onClick={upgradeAnnual}
                disabled={busy}
                className="bm-btn bm-btn-gold"
                style={{ padding: "6px 14px", fontSize: 13, opacity: busy ? 0.6 : 1 }}
            >
                {busy ? "Boarding…" : "Keep Pro — $49.99/yr"}
            </button>
            <button
                data-testid="trial-ending-plans-link"
                onClick={() => (isNativeApp() ? openPaywall() : nav("/pricing"))}
                className="bm-link"
                style={{ fontSize: 13, background: "none", border: "none", cursor: "pointer", color: "var(--bm-text-muted)", textDecoration: "underline" }}
            >
                All plans
            </button>
            <button
                data-testid="trial-ending-dismiss-btn"
                onClick={dismiss}
                aria-label="Dismiss"
                style={{
                    position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer", color: "var(--bm-text-dim)",
                    padding: 6, display: "flex",
                }}
            >
                <X size={15} />
            </button>
        </div>
    );
}
